// ========================================
// pages/AcceptTrustee.js - Accept Trustee Invitation
// ========================================

import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

const AcceptTrustee = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [trusteeInfo, setTrusteeInfo] = useState(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    fetchInvitationData();
  }, []);

  const fetchInvitationData = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/trustees/accept/${token}`);
      setTrusteeInfo(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid or expired invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
  setAccepting(true);
  try {
    // Store inviterName from current trusteeInfo before it gets overwritten
    const inviterName = trusteeInfo?.inviterName;
    
    const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/trustees/accept/${token}`);
    setSuccess(true);
    // Preserve the inviterName in the state
    setTrusteeInfo({
      ...response.data.trustee,
      inviterName: inviterName
    });
  } catch (err) {
    setError(err.response?.data?.error || 'Failed to accept invitation');
  } finally {
    setAccepting(false);
  }
};

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h2>Loading invitation...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h2 style={styles.errorTitle}>Invitation Error</h2>
          <p style={styles.errorText}>{error}</p>
          <p style={styles.note}>
            This invitation may have expired or already been used.
          </p>
          <Link to="/" style={styles.homeButton}>
            Go to Homepage
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.successIcon}>✅</div>
          <h2 style={styles.successTitle}>Invitation Accepted!</h2>
          <p style={styles.successText}>
            You are now a verified trustee for <strong>{trusteeInfo?.inviterName}</strong>.
          </p>
          <div style={styles.infoBox}>
            <h3>What happens next?</h3>
            <p>
              You don't need to do anything now. If the account owner misses their 
              90-day check-in, you'll receive an email with instructions to verify 
              their death.
            </p>
            <p>
              Your role is simply to confirm they are deceased. You will not see 
              any of their assets or personal information.
            </p>
          </div>
          <Link to="/" style={styles.homeButton}>
            Go to Homepage
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={styles.title}>Trustee Invitation</h2>
        
        <div style={styles.infoBox}>
  <p><strong>You have been invited to serve as a trustee for {trusteeInfo?.inviterName}.</strong></p>
  <p>Your Name: {trusteeInfo?.trusteeName}</p>
  <p>Your Email: {trusteeInfo?.trusteeEmail}</p>
</div>

        <div style={styles.roleBox}>
          <h3>Your Role as a Trustee</h3>
          <ul>
            <li>Verify death if the user misses 90-day check-ins</li>
            <li>You will NOT see any assets or personal information</li>
            <li>You can change your response later if needed</li>
          </ul>
        </div>

        <div style={styles.buttonGroup}>
          <button
            onClick={handleAccept}
            disabled={accepting}
            style={styles.acceptButton}
          >
            {accepting ? 'Processing...' : '✓ Accept Invitation'}
          </button>
          <Link to="/" style={styles.declineButton}>
            Decline
          </Link>
        </div>

        <p style={styles.note}>
          By accepting, you agree to act as a trustee in good faith.
        </p>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#F5F0E6',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px'
  },
  card: {
    backgroundColor: 'white',
    padding: '40px',
    borderRadius: '10px',
    maxWidth: '500px',
    width: '100%',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
    textAlign: 'center'
  },
  title: {
    fontSize: '24px',
    color: '#FFD700',
    marginBottom: '20px'
  },
  infoBox: {
    backgroundColor: '#F5F0E6',
    padding: '15px',
    borderRadius: '8px',
    marginBottom: '20px',
    textAlign: 'left'
  },
  roleBox: {
    backgroundColor: '#e8f4fd',
    padding: '15px',
    borderRadius: '8px',
    marginBottom: '20px',
    textAlign: 'left'
  },
  buttonGroup: {
    display: 'flex',
    gap: '15px',
    marginBottom: '20px'
  },
  acceptButton: {
    flex: 1,
    padding: '12px',
    backgroundColor: '#FFD700',
    color: '#1A1A1A',
    border: 'none',
    borderRadius: '5px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer'
  },
  declineButton: {
    flex: 1,
    padding: '12px',
    backgroundColor: 'transparent',
    color: '#666',
    border: '1px solid #666',
    borderRadius: '5px',
    textDecoration: 'none',
    fontSize: '16px',
    textAlign: 'center'
  },
  note: {
    fontSize: '12px',
    color: '#666'
  },
  errorTitle: {
    fontSize: '20px',
    color: '#dc2626',
    marginBottom: '15px'
  },
  errorText: {
    color: '#666',
    marginBottom: '20px'
  },
  successIcon: {
    fontSize: '48px',
    marginBottom: '20px'
  },
  successTitle: {
    fontSize: '24px',
    color: '#2E7D32',
    marginBottom: '15px'
  },
  successText: {
    color: '#666',
    marginBottom: '30px',
    lineHeight: '1.6'
  },
  homeButton: {
    display: 'inline-block',
    padding: '12px 30px',
    backgroundColor: '#FFD700',
    color: '#1A1A1A',
    textDecoration: 'none',
    borderRadius: '5px',
    fontWeight: 'bold'
  }
};

export default AcceptTrustee;